// src/contexts/AuthContext.js
import React, { createContext, useState, useContext, useEffect } from 'react';
import { clearAllDrafts } from '../utils/drafts';
import { 
  signInWithEmailAndPassword, 
  signInWithPopup, 
  GoogleAuthProvider,
  signOut, 
  onAuthStateChanged,
  sendPasswordResetEmail,
  updatePassword
} from 'firebase/auth';
import { doc, getDoc, setDoc, updateDoc } from 'firebase/firestore';
import { auth, db } from '../firebase/config';
import { can as roleCan, resolveAdjusterRole, companiesOf } from '../utils/permissions';

const AuthContext = createContext();

// Admins: acceso total (crear, editar, eliminar, pagos), ven todas las
// empresas. Es el custom claim `admin: true` de Firebase Auth
// (scripts/set-admin-claims.js) O, durante la transición (SECURITY.md S-06),
// esta lista chica hardcodeada, que también vive en isAdmin() de
// firestore.rules y en public/api/_security.php (sincronizar a mano si cambia).
//
// Ajustadores: solo lectura, limitados a su empresa. YA NO están
// hardcodeados aquí — cada uno vive en la colección Firestore `adjusters`,
// con un puntero en `adjusterAccess/{email}` cuando tiene acceso al portal
// (ver src/services/adjusterService.js), gestionado desde la pestaña
// "Ajustadores" del panel admin. Esto es lo que permite registrar un
// ajustador nuevo sin tocar código ni redesplegar.
const AUTHORIZED_ADMIN_EMAILS = [
  'info@csrclaimsassistant.com',
  'josejledezma.a@gmail.com'
];

export const useAuth = () => useContext(AuthContext);

export const AuthProvider = ({ children }) => {
  const [currentUser, setCurrentUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isAuthorized, setIsAuthorized] = useState(false);
  const [userRole, setUserRole] = useState(null);
  const [userCompany, setUserCompany] = useState(null);
  const [mustChangePassword, setMustChangePassword] = useState(false);
  const [userAdjusterName, setUserAdjusterName] = useState(null);
  const [userCanViewAllClaims, setUserCanViewAllClaims] = useState(false);
  const [userRoleKey, setUserRoleKey] = useState(null);
  const [userCompanies, setUserCompanies] = useState([]);

  // Ajustadores ya no viven en un objeto en memoria — su autorización exige
  // una lectura a Firestore (adjusterAccess/{email}, el puntero que
  // adjusterService.js mantiene sincronizado con el documento en
  // `adjusters`), y firestore.rules solo permite esa lectura para un
  // usuario YA autenticado. Por eso el chequeo de autorización para
  // no-admins pasa a hacerse DESPUÉS de signInWithEmailAndPassword/
  // signInWithPopup, no antes como con la lista hardcodeada anterior — un
  // correo random ahora sí llega a intentar el login en Firebase Auth antes
  // de ser rechazado, en vez de cortarse en el acto, pero es el precio de
  // que el allowlist ya no esté en el bundle.
  const getAdjusterMeta = async (email) => {
    try {
      const accessRef = doc(db, 'adjusterAccess', email);
      const snap = await getDoc(accessRef);
      if (snap.exists() && snap.data().active !== false) {
        const data = snap.data();
        return {
          role: 'adjuster',
          company: data.company || null,
          mustChangePassword: data.mustChangePassword === true,
          adjusterName: data.name || null,
          // Rol granular (ROLES_PLAN.md). Punteros anteriores sin `role` se infieren.
          roleKey: resolveAdjusterRole(data),
          // Ver todos los reclamos de su compañía: supervisor anterior (viewAllClaims) y, por rol,
          // P.A Admin y CSR Assistant (las reglas de Firestore ya lo dan igual: adjusterCanViewAll()).
          canViewAllClaims: data.viewAllClaims === true || ['pa_admin', 'csr_assistant'].includes(resolveAdjusterRole(data)),
          companies: companiesOf(data)
        };
      }
    } catch (error) {
      console.warn('⚠️ Error checking adjuster authorization:', error);
    }
    return null;
  };

  // Obtener el rol/empresa asignados a un email — admin es una lista fija
  // en memoria, ajustador es una lectura a Firestore.
  // Administrador = custom claim `admin: true` (scripts/set-admin-claims.js) O
  // correo de la lista antigua (transición, SECURITY.md S-06). El claim viaja
  // en el ID token: se ve tras volver a iniciar sesión o refrescar el token.
  const hasAdminClaim = async (user) => {
    try {
      const { claims } = await user.getIdTokenResult();
      return claims.admin === true;
    } catch (error) {
      return false;
    }
  };

  const getUserMeta = async (email, user) => {
    if (AUTHORIZED_ADMIN_EMAILS.includes(email) || (user && await hasAdminClaim(user))) {
      return { role: 'admin', company: null, mustChangePassword: false, adjusterName: null, canViewAllClaims: true, roleKey: 'super_admin', companies: [] };
    }
    return (await getAdjusterMeta(email)) || { role: null, company: null, mustChangePassword: false, adjusterName: null, canViewAllClaims: false, roleKey: null, companies: [] };
  };

  // Verificar si un email está autorizado (admin o ajustador activo)
  const checkAuthorization = async (email) => {
    const { role } = await getUserMeta(email);
    return role !== null;
  };

  // Login con email y password
  const login = async (email, password) => {
    try {
      setError('');

      const userCredential = await signInWithEmailAndPassword(auth, email, password);
      const user = userCredential.user;
      const { role, company, mustChangePassword: needsChange, adjusterName, canViewAllClaims, roleKey, companies } = await getUserMeta(user.email, user);

      if (!role) {
        await signOut(auth);
        return {
          success: false,
          error: 'Acceso no autorizado. Este correo no tiene permisos para acceder al sistema.'
        };
      }

      // Guardar información del usuario en Firestore
      const userRef = doc(db, 'users', user.uid);
      const userDoc = await getDoc(userRef);

      if (!userDoc.exists()) {
        await setDoc(userRef, {
          email: user.email,
          role,
          company: company || null,
          createdAt: new Date().toISOString(),
          lastLogin: new Date().toISOString(),
          isAuthorized: true
        });
      } else {
        await setDoc(userRef, { lastLogin: new Date().toISOString(), role, company: company || null }, { merge: true });
      }

      setUserRole(role);
      setUserCompany(company || null);
      setMustChangePassword(needsChange === true);
      setUserAdjusterName(adjusterName || null);
      setUserCanViewAllClaims(canViewAllClaims === true);
      setUserRoleKey(roleKey || null);
      setUserCompanies(companies || []);
      return { success: true, user, role, company: company || null };
    } catch (error) {
      console.error('Error en login:', error);
      let errorMessage = 'Error al iniciar sesión';
      
      switch (error.code) {
        case 'auth/user-not-found':
          errorMessage = 'Usuario no encontrado';
          break;
        case 'auth/wrong-password':
          errorMessage = 'Contraseña incorrecta';
          break;
        case 'auth/invalid-email':
          errorMessage = 'Correo electrónico inválido';
          break;
        case 'auth/too-many-requests':
          errorMessage = 'Demasiados intentos. Intenta más tarde';
          break;
        default:
          errorMessage = error.message;
      }
      
      return { success: false, error: errorMessage };
    }
  };

  // Login con Google
  const loginWithGoogle = async () => {
    try {
      setError('');
      const provider = new GoogleAuthProvider();
      const result = await signInWithPopup(auth, provider);
      const user = result.user;

      // Verificar si el email está autorizado (admin o ajustador activo)
      const { role, company, mustChangePassword: needsChange, adjusterName, canViewAllClaims, roleKey, companies } = await getUserMeta(user.email, user);
      if (!role) {
        await signOut(auth);
        return {
          success: false,
          error: 'Acceso no autorizado. Este correo no tiene permisos para acceder al sistema.'
        };
      }

      // Guardar información del usuario en Firestore
      const userRef = doc(db, 'users', user.uid);
      const userDoc = await getDoc(userRef);

      if (!userDoc.exists()) {
        await setDoc(userRef, {
          email: user.email,
          name: user.displayName,
          photoURL: user.photoURL,
          role,
          company: company || null,
          createdAt: new Date().toISOString(),
          lastLogin: new Date().toISOString(),
          isAuthorized: true
        });
      } else {
        await setDoc(userRef, { lastLogin: new Date().toISOString(), role, company: company || null }, { merge: true });
      }

      setUserRole(role);
      setUserCompany(company || null);
      setUserAdjusterName(adjusterName || null);
      setUserCanViewAllClaims(canViewAllClaims === true);
      setUserRoleKey(roleKey || null);
      setUserCompanies(companies || []);
      // Google ya administra la credencial; no se fuerza una contraseña
      // local adicional. Se completa el onboarding de este acceso.
      if (needsChange && role === 'adjuster') {
        await updateDoc(doc(db, 'adjusterAccess', user.email), { mustChangePassword: false });
      }
      setMustChangePassword(false);
      return { success: true, user, role, company: company || null };
    } catch (error) {
      console.error('Error en login con Google:', error);
      return { success: false, error: error.message };
    }
  };

  // Logout
  const logout = async () => {
    try {
      clearAllDrafts(); // los borradores no deben sobrevivir a la cuenta que los escribió
      await signOut(auth);
      setUserRole(null);
      setUserCompany(null);
      setMustChangePassword(false);
      setUserAdjusterName(null);
      setUserCanViewAllClaims(false);
      setUserRoleKey(null);
      setUserCompanies([]);
      return { success: true };
    } catch (error) {
      console.error('Error en logout:', error);
      return { success: false, error: error.message };
    }
  };

  // Reset password
  const resetPassword = async (email) => {
    try {
      await sendPasswordResetEmail(auth, email);
      return { success: true };
    } catch (error) {
      console.error('Error en reset password:', error);
      return { success: false, error: error.message };
    }
  };

  const changeInitialPassword = async (newPassword) => {
    const user = auth.currentUser;
    if (!user?.email) return { success: false, error: 'No hay una sesión activa.' };
    try {
      await updatePassword(user, newPassword);
      await updateDoc(doc(db, 'adjusterAccess', user.email), { mustChangePassword: false });
      setMustChangePassword(false);
      return { success: true };
    } catch (error) {
      console.error('Error changing initial password:', error);
      return { success: false, error: error.message };
    }
  };

  // Observador de estado de autenticación
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      if (user) {
        // Verificar si el usuario está autorizado
        const { role, company, mustChangePassword: needsChange, adjusterName, canViewAllClaims, roleKey, companies } = await getUserMeta(user.email, user);
        if (role) {
          setCurrentUser(user);
          setIsAuthenticated(true);
          setIsAuthorized(true);
          setUserRole(role);
          setUserCompany(company || null);
          setMustChangePassword(needsChange === true);
          setUserAdjusterName(adjusterName || null);
          setUserCanViewAllClaims(canViewAllClaims === true);
          setUserRoleKey(roleKey || null);
          setUserCompanies(companies || []);
      setUserRoleKey(roleKey || null);
      setUserCompanies(companies || []);

          // Actualizar última conexión
          const userRef = doc(db, 'users', user.uid);
          await setDoc(userRef, {
            lastLogin: new Date().toISOString(),
            email: user.email,
            role,
            company: company || null
          }, { merge: true });
        } else {
          // Usuario no autorizado - cerrar sesión
          await signOut(auth);
          setCurrentUser(null);
          setIsAuthenticated(false);
          setIsAuthorized(false);
          setUserRole(null);
          setUserCompany(null);
          setMustChangePassword(false);
          setUserAdjusterName(null);
          setUserCanViewAllClaims(false);
          setUserRoleKey(null);
          setUserCompanies([]);
          setError('Acceso no autorizado');
        }
      } else {
        setCurrentUser(null);
        setIsAuthenticated(false);
        setIsAuthorized(false);
        setUserRole(null);
        setUserCompany(null);
        setMustChangePassword(false);
        setUserAdjusterName(null);
        setUserCanViewAllClaims(false);
        setUserRoleKey(null);
        setUserCompanies([]);
      }
      setLoading(false);
    });

    return unsubscribe;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const value = {
    currentUser,
    login,
    loginWithGoogle,
    logout,
    resetPassword,
    changeInitialPassword,
    error,
    setError,
    loading,
    isAuthenticated,
    isAuthorized,
    checkAuthorization,
    userRole,
    userCompany,
    mustChangePassword,
    userAdjusterName,
    userCanViewAllClaims,
    userRoleKey,
    userCompanies,
    can: capability => roleCan(userRoleKey, capability),
    isAdmin: userRole === 'admin',
    isAdjuster: userRole === 'adjuster'
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export default AuthContext;
